import { fitBetween } from '../../core/utils/fitBetween.js';
import { inverseLerp, lerp } from '../../core/utils/interpolate.js';
import {
  THRESHOLD_INTERVAL_MAX_GAP,
  THRESHOLD_INTERVAL_MAX_OFFSET,
  THRESHOLD_INTERVAL_MAX_THICKNESS,
  THRESHOLD_INTERVAL_MIN_GAP,
  THRESHOLD_INTERVAL_MIN_OFFSET,
  THRESHOLD_INTERVAL_MIN_THICKNESS,
  THRESHOLD_INTERVAL_SCALE_MAX_ARC_WIDTH,
  THRESHOLD_INTERVAL_SCALE_MIN_ARC_WIDTH,
} from '../constants.js';

export interface ThresholdBandGeometry {
  innerRadius: number;
  outerRadius: number;
  midRadius: number;
  gap: number;
}

/** Maps `arcWidth` to a 0–1 scale factor, clamped to the configured min/max arc width range. */
const normalizeArcWidthToScale = (arcWidth: number): number => {
  const scale = inverseLerp(
    THRESHOLD_INTERVAL_SCALE_MIN_ARC_WIDTH,
    THRESHOLD_INTERVAL_SCALE_MAX_ARC_WIDTH,
    arcWidth,
  );
  return fitBetween(scale, 0, 1);
};

/**
 * Computes the geometry of the threshold band arc drawn just inside the gauge arc.
 *
 * All visual parameters (offset from arc, gap between segments, band thickness)
 * scale linearly with the arc width so the band looks proportional at any size.
 */
export const computeThresholdBandGeometry = (
  arcInnerRadius: number,
  arcOuterRadius: number,
): ThresholdBandGeometry => {
  const arcWidth = arcOuterRadius - arcInnerRadius;
  const scale = normalizeArcWidthToScale(arcWidth);

  const offset = lerp(
    THRESHOLD_INTERVAL_MIN_OFFSET,
    THRESHOLD_INTERVAL_MAX_OFFSET,
    scale,
  );
  const gap = lerp(
    THRESHOLD_INTERVAL_MIN_GAP,
    THRESHOLD_INTERVAL_MAX_GAP,
    scale,
  );
  const thickness = lerp(
    THRESHOLD_INTERVAL_MIN_THICKNESS,
    THRESHOLD_INTERVAL_MAX_THICKNESS,
    scale,
  );

  const outerRadius = arcInnerRadius - offset;
  const innerRadius = outerRadius - thickness;
  const midRadius = (outerRadius + innerRadius) / 2;

  return { innerRadius, outerRadius, midRadius, gap };
};
